import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { suggestGroupsForLead, convertLead } from "@/api/leads";
import { extractErrorMessage } from "@/types/common";
import type { Lead } from "@/types/lead";

export function ConvertLeadDialog({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [groupId, setGroupId] = useState("");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [serverError, setServerError] = useState<string | null>(null);

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ["lead-suggestions", lead?.id],
    queryFn: () => suggestGroupsForLead(lead!.id),
    enabled: Boolean(lead),
  });

  const mutation = useMutation({
    mutationFn: () => convertLead(lead!.id, { groupId, startDate }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["leads"] });
      setGroupId("");
      onClose();
    },
    onError: (error) => setServerError(extractErrorMessage(error)),
  });

  return (
    <Dialog open={Boolean(lead)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Зачислить «{lead?.fullName}» в группу</DialogTitle>
      <DialogContent>
        {serverError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {serverError}
          </Alert>
        )}
        <Stack spacing={2} mt={1}>
          <TextField
            label="Дата начала обучения"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle2">Подходящие активные группы по предмету и филиалу лида</Typography>
          {isLoading && <Typography variant="body2">Загрузка групп...</Typography>}
          {!isLoading && (suggestions ?? []).length === 0 && (
            <Alert severity="warning">Нет свободных активных групп по этому предмету в этом филиале. Создайте новую группу.</Alert>
          )}
          <RadioGroup value={groupId} onChange={(e) => setGroupId(e.target.value)}>
            {(suggestions ?? []).map((s) => (
              <FormControlLabel
                key={s.group.id}
                value={s.group.id}
                control={<Radio />}
                label={`${s.group.code} · ${s.group.level} · ${s.group.timeSlot} (свободно мест: ${s.seatsLeft})`}
              />
            ))}
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!groupId || mutation.isPending} onClick={() => mutation.mutate()}>
          Зачислить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
